import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  applyClientWalkthroughTransition,
  currentRecipesWalkthroughStep,
  getRecipesWalkthroughProductGate,
  postRecipesWalkthroughInteraction,
  recipesWalkthroughNeedsResume,
  shouldShowRecipesWalkthrough,
} from 'src/utils/growthEngineWalkthrough'

const warn = (label, err) => {
  console.warn(`Growth Engine ${label} failed`, err)
}

/**
 * Recipes first-success walkthrough. Steps come from Express — never invented.
 * Hidden until the first-try card's CTA calls `open()`, so the page shows one
 * first-use block. Skip hides chrome; open again resumes with completed steps.
 */
export default function useGrowthEngineRecipesWalkthrough({
  venueId,
  guidedSuccess,
  suggestedItems,
} = {}) {
  const [localProgress, setLocalProgress] = useState(null)
  const [skippedHere, setSkippedHere] = useState(false)
  const [sessionLive, setSessionLive] = useState(false)

  useEffect(() => {
    setLocalProgress(null)
    setSkippedHere(false)
    setSessionLive(false)
  }, [venueId, guidedSuccess?.featureId])

  const steps = useMemo(
    () =>
      Array.isArray(guidedSuccess?.walkthrough?.steps)
        ? guidedSuccess.walkthrough.steps
        : [],
    [guidedSuccess],
  )
  const progress = localProgress || guidedSuccess?.walkthrough?.progress || null
  const walkthrough = steps.length ? { steps, progress } : null
  const productGate = getRecipesWalkthroughProductGate(suggestedItems)

  const visible =
    shouldShowRecipesWalkthrough({
      ...guidedSuccess,
      walkthrough,
    }) &&
    sessionLive &&
    !skippedHere &&
    productGate.showWalkthrough

  const currentStep = currentRecipesWalkthroughStep(walkthrough)
  const stepIndex = Math.max(
    0,
    steps.findIndex(step => step.id === currentStep?.id),
  )
  const isLast = steps.length > 0 && stepIndex === steps.length - 1
  const needsResume = recipesWalkthroughNeedsResume(progress) && !sessionLive

  const apply = useCallback(
    (interaction, stepId, fromProgress = progress) => {
      const result = applyClientWalkthroughTransition({
        steps,
        progress: fromProgress,
        interaction,
        stepId,
      })
      if (!result.ok) return result
      setLocalProgress(result.progress)
      if (interaction === 'walkthrough_skipped') setSkippedHere(true)
      if (
        interaction === 'walkthrough_started' ||
        interaction === 'walkthrough_resumed' ||
        interaction === 'walkthrough_step_completed'
      ) {
        setSessionLive(true)
      }
      postRecipesWalkthroughInteraction(
        venueId,
        interaction,
        stepId ? { stepId } : {},
      ).catch(err => warn(interaction, err))
      return result
    },
    [progress, steps, venueId],
  )

  const start = useCallback(() => {
    if (!steps.length) return null
    if (
      progress?.status === 'in_progress' ||
      progress?.status === 'completed' ||
      progress?.status === 'skipped'
    ) {
      return null
    }
    return apply('walkthrough_started')
  }, [apply, progress?.status, steps.length])

  const resume = useCallback(() => {
    if (!steps.length) return null
    return apply('walkthrough_resumed')
  }, [apply, steps.length])

  const open = useCallback(() => {
    if (!steps.length || !productGate.showWalkthrough) return null
    if (progress?.status === 'completed' || sessionLive || skippedHere) {
      return null
    }
    if (progress?.status === 'in_progress' || progress?.status === 'skipped') {
      return resume()
    }
    return start()
  }, [
    productGate.showWalkthrough,
    progress?.status,
    resume,
    sessionLive,
    skippedHere,
    start,
    steps.length,
  ])

  const skip = useCallback(() => {
    if (!steps.length) return null
    return apply('walkthrough_skipped')
  }, [apply, steps.length])

  const completeStep = useCallback(
    stepId => {
      if (!stepId) return null
      if (progress?.status === 'skipped' || progress?.status === 'completed') {
        return null
      }
      let fromProgress = progress
      if (fromProgress?.status !== 'in_progress') {
        const started = apply('walkthrough_started', undefined, fromProgress)
        if (started?.ok) fromProgress = started.progress
      }
      return apply('walkthrough_step_completed', stepId, fromProgress)
    },
    [apply, progress],
  )

  const next = useCallback(() => {
    if (!productGate.allowNext) return null
    if (!currentStep) return null
    if (isLast) return apply('walkthrough_completed')
    return completeStep(currentStep.id)
  }, [apply, completeStep, currentStep, isLast, productGate.allowNext])

  return {
    visible,
    steps,
    currentStep,
    stepIndex,
    stepCount: steps.length,
    progress,
    needsResume,
    isLast,
    allowNext: productGate.allowNext,
    showStepChrome: productGate.showStepChrome,
    start,
    open,
    resume,
    skip,
    next,
    completeStep,
  }
}
